/* eslint-disable react-hooks/exhaustive-deps */
import debounce from "debounce-promise"
import React, {KeyboardEventHandler, useState} from "react"
import {useTranslation} from "react-i18next"
import {userService, userServiceV3} from "services"
import {KlassDropAsyncPaginate, KlassDropdownAsyncProps} from "../Select"
import {UserAutocomplete} from "types/user"
import {getFullName} from "helpers"
import styles from "./UserPaginationSelect.module.css"
import {Auth} from "types/auth"

type Props = Partial<KlassDropdownAsyncProps> & {
    type?: Auth.UserProfileType | Auth.UserProfileType[]
    isActive?: boolean | boolean[]
    supportCustomProfileIdSearch?: boolean
    campusIds?: number[]
    studentStatusIds?: number[]
    states?: Auth.UserProfileState[]
}

const transformUserData = (userData: any[]): UserAutocomplete[] => {
    const transformed: UserAutocomplete[] = []

    userData.forEach((user) => {
        if (user.profiles && user.profiles.length > 0) {
            user.profiles.forEach((profile) => {
                transformed.push({
                    userId: user.id,
                    profileId: profile.id,
                    customProfileId: profile.customProfileId,
                    state: profile.state,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    customUserId: user.customUserId,
                    photo: user.photo,
                    type: profile.type,
                    isAccessDisabled: profile.active === 0 ? 1 : 0,
                    name: user.fullName || user.name,
                    campusId: profile.campus?.id
                } as UserAutocomplete)
            })
        }
    })

    return transformed
}

const UserSelect: React.FC<Props> = ({
    type,
    isActive,
    campusIds,
    studentStatusIds = [],
    states = [],
    supportCustomProfileIdSearch = false,
    ...props
}) => {
    const {t} = useTranslation(["common"])
    const [inputValue, setInputValue] = useState<string>("")

    const search = React.useCallback(
        debounce(async (search: string = "", loadedOptions: UserAutocomplete[]) => {
            const pageSize = 20
            const page = Math.ceil(loadedOptions.length / pageSize) + 1
            const {data, additionalData} = await userServiceV3.getAllAutocomplete({
                filter: {
                    withProfiles: true,
                    withRestUsers: true,
                    withProfilesCampuses: true,
                    type,
                    profileStatus: isActive,
                    search,
                    campuses: campusIds,
                    ...(studentStatusIds.length ? {studentStatusIds} : {}),
                    ...(states.length ? {states} : {})
                },
                range: {
                    page,
                    pageSize
                }
            })

            const transformedData = transformUserData(data)
            const total = transformedData.length + (additionalData?.restUserIds?.length || 0)

            return {
                options: transformedData,
                hasMore: loadedOptions.length < total
            }
        }, 500),
        [type, isActive, campusIds, studentStatusIds]
    )

    const cacheKey = React.useMemo(() => {
        return JSON.stringify({
            type,
            isActive,
            campusIds,
            studentStatusIds
        })
    }, [type, isActive, campusIds, studentStatusIds])

    const handleInputChange = (_inputValue: string) => {
        setInputValue(_inputValue)
    }

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = async (event) => {
        if (!inputValue || !supportCustomProfileIdSearch) return

        switch (event.key) {
            case "Enter":
            case "Tab":
                const values = inputValue.trim().split(",")
                if (values.length > 1) {
                    const {data} = await userService.searchUsers({
                        filters: {
                            type,
                            isActive,
                            customProfileIds: values
                        },
                        range: {
                            page: 1,
                            pageSize: values.length
                        }
                    })
                    props.onChange((props.value || []).concat(data))
                    setInputValue("")
                    event.preventDefault()
                }
        }
    }

    return (
        <KlassDropAsyncPaginate
            {...props}
            key={cacheKey}
            placeholder={props.placeholder || t("message.searchByNameOrEmail")}
            loadOptions={search}
            defaultOptions
            valueKey="profileId"
            labelKey="name"
            getOptionLabel={UserOptionLabel}
            onKeyDown={handleKeyDown}
            onInputChange={handleInputChange}
            inputValue={inputValue}
        />
    )
}

export default UserSelect

export const UserOptionLabel = (option: UserAutocomplete) => {
    return (
        <span>
            <span className={styles.userId}>{option.customProfileId}</span>
            {getFullName(option) || option.name}
            {option.type === Auth.UserProfileType.Student && !!option.state && (
                <span className={styles.userType}> ({option.state})</span>
            )}
        </span>
    )
}
